/* eslint-disable react/static-property-placement */
import { FC, lazy, Suspense, useCallback, useEffect } from 'react';
import { BrowserRouter, Navigate, Routes, Route } from 'react-router-dom';

import Config from 'Config';
import Location from 'services/Location';
import { buildOauthQuery } from 'helpers/oath-query-builder';

const Signin = lazy(() =>
  import('routes/signin/Signin').then((mod) => ({
    default: mod.Signin,
  }))
);
const Chooser = lazy(() =>
  import('routes/chooser/Chooser').then((mod) => ({
    default: mod.Chooser,
  }))
);
const CustomSSO = lazy(() =>
  import('routes/custom-sso/CustomSSO').then((mod) => ({
    default: mod.CustomSSO,
  }))
);
const Signup = lazy(() =>
  import('routes/signup/Signup').then((mod) => ({
    default: mod.Signup,
  }))
);
const LoginButton = lazy(() =>
  import('routes/login-button/LoginButton').then((mod) => ({
    default: mod.LoginButton,
  }))
);
const ForgotPassword = lazy(() =>
  import('routes/forgot-password/ForgotPassword').then((mod) => ({
    default: mod.ForgotPassword,
  }))
);
const SetPassword = lazy(() =>
  import('routes/set-password/SetPassword').then((mod) => ({
    default: mod.SetPassword,
  }))
);
const ActivateAccount = lazy(() =>
  import('routes/activate-account/ActivateAccount').then((mod) => ({
    default: mod.ActivateAccount,
  }))
);
const Consent = lazy(() =>
  import('routes/consent/Consent').then((mod) => ({
    default: mod.Consent,
  }))
);
const Ooops = lazy(() =>
  import('routes/ooops/Ooops').then((mod) => ({
    default: mod.Ooops,
  }))
);
const Join = lazy(() =>
  import('routes/join/Join').then((mod) => ({
    default: mod.Join,
  }))
);
const SetEmail = lazy(() =>
  import('routes/set-email/SetEmail').then((mod) => ({
    default: mod.SetEmail,
  }))
);
const Wizard = lazy(() =>
  import('routes/wizard/Wizard').then((mod) => ({
    default: mod.Wizard,
  }))
);
const Verification = lazy(() =>
  import('routes/verification/Verification').then((mod) => ({
    default: mod.Verification,
  }))
);
const ActivateProduct = lazy(() =>
  import('routes/activate-product/ActivateProduct').then((mod) => ({
    default: mod.ActivateProduct,
  }))
);
const StopScreen = lazy(() =>
  import('routes/stop-screen/StopScreen').then((mod) => ({
    default: mod.StopScreen,
  }))
);
const Logout = lazy(() =>
  import('routes/logout-screen/Logout').then((mod) => ({
    default: mod.Logout,
  }))
);
const SelectTrial = lazy(() =>
  import('routes/select-trial/SelectTrial').then((mod) => ({
    default: mod.SelectTrial,
  }))
);

const ROUTES = [
  '', // first route is default
  'custom-sso',
  'signup',
  'select-trial',
  'forgot-password',
  'set-password',
  'activate-product',
  'activate',
  'logout',
  'consent',
  'ooops',
  'login-button/success',
  'login-button/error',
  'join',
  'set-email',
  'choose-organization',
  'wizard',
  'verification',
  'stop-screen',
];

const ROUTE_REDIRECTS = {
  signin: '/',
  'signup/credentials': '/signup',
  'signup/website': '/signup',
  'signup/company': '/signup',
  'reset-password': '/set-password',
  'wrong-sso': '/?identity_exception=wrong_sso',
};

const REQUIRE_LOGIN_ROUTES = ['activate-product', 'choose-organization', 'set-email', 'wizard'];

const getRouteFromUrl = (): string => {
  const currentUrl = Location.getPathName();

  return ROUTES.find((route, index) => {
    if (index === 0) {
      return route === currentUrl;
    }

    return new RegExp(`^${route}`).test(currentUrl);
  });
};

const getDefaultRedirectRoute = (): string => ROUTES[0];

const isRouteDefault = (): boolean => Location.getPathName() === getDefaultRedirectRoute();

type Props = {
  isTokenValid: boolean;
};

export const Router: FC<Props> = ({ isTokenValid }) => {
  const followRedirects = useCallback((): void => {
    const url = Location.getPathName();

    if (ROUTE_REDIRECTS[url]) {
      const redirectUrl = `${window.location.origin}${ROUTE_REDIRECTS[url]}${window.location.search}`;
      window.location.href = redirectUrl;
    }

    if (
      (!getRouteFromUrl() && isRouteDefault()) ||
      (!isTokenValid && REQUIRE_LOGIN_ROUTES.includes(url))
    ) {
      let param;

      if (
        !Location.getUrlParam('client_id') &&
        !Location.getUrlParam('response_type') &&
        !Location.getUrlParam('redirect_uri')
      ) {
        param = {
          client_id: Config.client_id,
          redirect_uri:
            encodeURIComponent(Location.getPathName()) ||
            encodeURIComponent(import.meta.env.VITE_APP_DEFAULT_REDIRECT_URI),
          response_type: Config.response_type,
        };
      }

      const queryParams = param ? `?${buildOauthQuery(param)}` : window.location.search;
      const redirectRoute = `/${getDefaultRedirectRoute()}${queryParams}`;
      window.history.replaceState(null, '', redirectRoute);
    }
  }, [isTokenValid]);

  useEffect(() => {
    const handlePopState = (): void => {
      followRedirects();
    };

    window.addEventListener('popstate', handlePopState);
    followRedirects();

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [followRedirects]);

  return (
    <BrowserRouter>
      <Suspense fallback={null}>
        <Routes>
          <Route path="login-button/success" element={<LoginButton />} />
          <Route path="" element={<Signin />} />
          <Route path="choose-organization" element={<Chooser />} />
          <Route path="custom-sso/*" element={<CustomSSO />} />
          <Route path="signup" element={<Signup />} />
          <Route path="select-trial" element={<SelectTrial />} />
          <Route path="forgot-password" element={<ForgotPassword />} />
          <Route path="set-password/*" element={<SetPassword />} />
          <Route path="activate/*" element={<ActivateAccount />} />
          <Route path="consent" element={<Consent />} />
          <Route
            path="ooops"
            element={
              <Ooops
                flow={Location.getUrlParam('flow')}
                identityException={Location.getUrlParam('identity_exception')}
                oauthException={Location.getUrlParam('oauth_exception')}
                exceptionDetails={Location.getUrlParam('exception_details')}
                requestId={Location.getUrlParam('request_id')}
                traceId={Location.getUrlParam('trace_id')}
              />
            }
          />
          <Route path="join" element={<Join />} />
          <Route path="set-email" element={<SetEmail />} />
          <Route path="wizard" element={<Wizard />} />
          <Route path="verification" element={<Verification />} />
          <Route path="activate-product/*" element={<ActivateProduct />} />
          <Route path="stop-screen" element={<StopScreen />} />
          <Route path="logout" element={<Logout />} />
          <Route
            path="*"
            element={<Navigate to={`${ROUTE_REDIRECTS.signin}${window.location.search}`} replace />}
          />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
};
